import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {combineLatest, merge, Observable, of, ReplaySubject, Subject} from 'rxjs';
import {Router} from '@angular/router';
import {ChartQuantity, CUSTOM_DATE_FORMATS} from '../economics-aggregated/ec-economics-aggregated.component';
import * as moment from 'moment-timezone';
import {distinctUntilChanged, filter, first, shareReplay, switchMap, takeUntil} from 'rxjs/operators';
import {EnergyCommunity, EntityType} from 'smag-commons/cfg/hierarchy';
import {HierarchyEntity, HierarchyService} from '../../../shared/hierarchy.service';
import {SmagApiService} from '../../../api/smag-api.service';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import {MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter} from '@angular/material-moment-adapter';
import {ECEconomicsMarketResponse, EconomicsErrorType} from 'smag-commons/dto/eco';
import {APP_CONFIG_TOKEN, AppConfig} from '../../../app.config';
import {ResizeService} from '../../../shared/resize.service';
import {I18NService} from '../../../i18n/i18n.service';
import {isDefined} from '../../../commons/commons';
import {ConfigurationService} from '../../../shared/configuration.service';
import {can} from '../../../shared/permissions';
import {Permission} from 'smag-commons/dto/api';
import {hasError, showError} from '../../../shared/economics-errors';
import {TIME_DELTA_HOUR} from '../../../shared/line-chart/line-chart.pipe';
import {BillingPeriod, BillingPeriodConfig} from '../billing-filter/ec-billing-filter.component';
import {environment} from '../../../../environments/environment';
import {ECMetricStats} from 'smag-commons/dto/eco/community';
import {TimeSlot} from 'smag-commons/cfg';
import {isViewable, mkBillingPeriodParams, noData, sanityFromTimeActivationDate, sanityToTime} from '../../../shared/ec-economics';
import {ComputationStatusService} from '../../../shared/compute-status.service';

interface MarketChartQuantity extends ChartQuantity {
    percentage_stats: { [timeSlot in TimeSlot]: ECMetricStats } | ECMetricStats;
}

const ZONE_PRICE_NAME = 'Zone price';
const PUN_NAME = 'PUN';
const ZONE_PUN_DIFFERENCE_NAME = 'Difference between zone price and PUN';

@Component({
    selector: 'app-market',
    templateUrl: './ec-market.component.html',
    styleUrls: ['./ec-market.component.scss'],
    providers: [
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
        {provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS}
    ]
})
export class ECMarketComponent implements OnInit, OnDestroy {

    private destroyed = new Subject<void>();
    private reloadMarketData = new Subject<void>();
    private billingPeriodSubj: ReplaySubject<BillingPeriod> = new ReplaySubject<BillingPeriod>(1);

    hierarchyEntity: Observable<HierarchyEntity> = null;
    timeZone: string = null;
    billingPeriodCfg: BillingPeriodConfig = null;
    billingPeriod: BillingPeriod = {} as BillingPeriod;
    response: ECEconomicsMarketResponse = null;
    contentResize: Observable<void>;
    quantities: MarketChartQuantity[] = null;
    checks = {[ZONE_PRICE_NAME]: true, [PUN_NAME]: true, [ZONE_PUN_DIFFERENCE_NAME]: true};
    futureEc = false;
    inactiveEc = false;
    showComputationWarning = false;

    // exported in HTML
    readonly EconomicsErrorType = EconomicsErrorType;
    readonly TIME_DELTA_HOUR = TIME_DELTA_HOUR;
    readonly ZONE_PRICE_NAME = ZONE_PRICE_NAME;
    readonly PUN_NAME = PUN_NAME;
    readonly ZONE_PUN_DIFFERENCE_NAME = ZONE_PUN_DIFFERENCE_NAME;
    readonly TimeSlot = TimeSlot;
    readonly hasError = hasError;
    readonly showError = showError;
    readonly isViewable = isViewable;

    constructor(@Inject(APP_CONFIG_TOKEN) private readonly cfg: AppConfig,
                private readonly hierarchyService: HierarchyService,
                private readonly apiService: SmagApiService,
                private readonly resizeService: ResizeService,
                private readonly i18n: I18NService,
                private readonly configurationService: ConfigurationService,
                private readonly computationService: ComputationStatusService,
                private readonly router: Router) {
        this.contentResize = this.resizeService.observe(['content']).pipe(takeUntil(this.destroyed));
    }

    ngOnInit() {
        this.configurationService.timeConfig
            .pipe(first())
            .subscribe(timeCfg => this.timeZone = timeCfg.time_zone);

        this.computationService.showComputingWarning
            .pipe(takeUntil(this.destroyed))
            .subscribe(bool => this.showComputationWarning = bool);

        // NOTE: as we store time range in URL, we need to avoid "navigation" to same entity when
        //       setting URL parameters for time-range
        this.hierarchyEntity = this.hierarchyService.selection.pipe(
            distinctUntilChanged((h1, h2) => h1?.id === h2?.id),
            shareReplay(1),
            takeUntil(this.destroyed)
        );

        combineLatest([this.hierarchyEntity, this.billingPeriodSubj])
            .pipe(takeUntil(this.destroyed))
            .subscribe(([hierarchyEntity, billingPeriod]) => this.mkData(hierarchyEntity, billingPeriod));

        this.hierarchyEntity.pipe(
            filter(hierarchyEntity => !!hierarchyEntity),
            switchMap(hierarchyEntity => can(hierarchyEntity.permissions, Permission.READ) ?
                this.apiService.getEntity(hierarchyEntity.id) :
                of(null)),
            takeUntil(this.destroyed)
        ).subscribe(entity => {
            // Facciamo in modo che il mese selezionato sia quello di inizio "fromTime", inseriamo nuovo parametro per disabilitare il selettore
            this.billingPeriodCfg = {
                pastDate: entity ? (entity as EnergyCommunity).activation_date || null : null,
                futureDate: null
            };
            if (!isDefined(this.billingPeriodCfg.pastDate)) {
                this.inactiveEc = true;
            } else {
                this.inactiveEc = false;
                this.futureEc = this.billingPeriodCfg.pastDate > moment.tz(this.timeZone).valueOf();
            }
        });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    onChangeBillingPeriod(period: BillingPeriod) {
        this.billingPeriodSubj.next(period);
        this.billingPeriod = period;
        this.router.navigate([],
            {
                queryParams: mkBillingPeriodParams(period),
                queryParamsHandling: 'merge'
            }).then();
    }

    mkData(entity: HierarchyEntity, billingPeriod: BillingPeriod) {
        this.response = null;
        if (entity?.type === EntityType.ENERGY_COMMUNITY) {
            // toTime can't be more than current time
            const to = sanityToTime(this.timeZone, billingPeriod.toTime);
            const from = sanityFromTimeActivationDate(billingPeriod.fromTime, this.billingPeriodCfg.pastDate, this.timeZone);
            this.reloadMarketData.next();
            this.computationService.checkComputationStatus(entity.id, moment.tz(billingPeriod.fromTime, this.timeZone).startOf('month').year())
                .pipe(takeUntil(merge(this.destroyed, this.reloadMarketData)))
                .subscribe(() => {
                    this.apiService.ecEconomicsMarket(entity.id, from, to)
                        .pipe(takeUntil(merge(this.destroyed, this.reloadMarketData)))
                        .subscribe(response => {
                            this.response = response;
                            this.quantities = this.mkCharts();
                        });
                });
        }
    }

    goBack() {
        this.router.navigate(['/c', 'ec', 'economics', 'aggregated'],
            {queryParams: {}, queryParamsHandling: 'merge'}).then();
    }

    get showNullData() {
        return this.inactiveEc || this.futureEc;
    }

    get gridColor(): string {
        return this.cfg.shared.gridColor;
    }

    get tooltipBorderColor(): string {
        return this.cfg.shared.tooltipBorderColor;
    }

    get tooltipBackgroundColor(): string {
        return this.cfg.shared.tooltipBackgroundColor;
    }

    get selectionLineColor(): string {
        return this.cfg.shared.selectionLineColor;
    }

    private mkCharts(): MarketChartQuantity[] {
        const qts: MarketChartQuantity[] = [
            {
                name: ZONE_PRICE_NAME,
                data: [{
                    data: this.response.times.map((t, i) => [t, this.response.pz[i]]),
                    style: {color: environment.appColors.ecoRed, strokeWidth: 1.5},
                    name: this.i18n.translate(ZONE_PRICE_NAME)
                }],
                uom: '€/MWh',
                percentage_stats: this.response.pz_stats
            },
            {
                name: PUN_NAME,
                data: [
                    {
                        data: this.response.times.map((t, i) => [t, this.response.pun[i]]),
                        style: {color: environment.appColors.ecoRed, strokeWidth: 1.5},
                        name: this.i18n.translate(PUN_NAME)
                    }
                ],
                uom: '€/MWh',
                percentage_stats: this.response.pun_stats
            },
            {
                name: ZONE_PUN_DIFFERENCE_NAME,
                data: [{
                    data: this.response.times.map((t, i) => [t, this.getZonalPriceAndPUNDifference(this.response.pz[i], this.response.pun[i])]),
                    style: {color: environment.appColors.ecoBlue, strokeWidth: 1.5},
                    name: this.i18n.translate(ZONE_PUN_DIFFERENCE_NAME)
                }],
                uom: '%',
                percentage_stats: this.response.diff_stats
            },
        ];
        return qts.map(qt => ({
            ...qt,
            data: noData(qt.data) ? [] : qt.data
        } as MarketChartQuantity));
    }

    private getZonalPriceAndPUNDifference(zonalPrice: number, pun: number): number {
        return (zonalPrice - pun) / pun * 100;
    }

}


